import React, { useState, useEffect, useRef } from 'react';
import { api } from '../api';

const getFitBadgeColor = (fit) => {
  switch (fit) {
    case 'high': return 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/50';
    case 'medium': return 'bg-yellow-500/20 text-yellow-400 border border-yellow-500/50';
    case 'low': return 'bg-red-500/20 text-red-400 border border-red-500/50';
    default: return 'bg-slate-700 text-slate-300';
  }
};

const fitLabel = (fit) => {
  switch (fit) {
    case 'high': return 'High';
    case 'medium': return 'Med';
    case 'low': return 'Low';
    default: return fit;
  }
};

function FitTooltip({ children, productName, reasoning }) {
  const [show, setShow] = useState(false);
  const [pos, setPos] = useState({ top: 0, left: 0 });
  const ref = useRef(null);

  const handleEnter = () => {
    if (!reasoning) return;
    const rect = ref.current.getBoundingClientRect();
    setPos({ top: rect.top - 8, left: rect.left + rect.width / 2 });
    setShow(true);
  };

  return (
    <>
      <span ref={ref} onMouseEnter={handleEnter} onMouseLeave={() => setShow(false)}>
        {children}
      </span>
      {show && reasoning && (
        <div
          className="fixed z-[9999] w-72 p-3 bg-slate-900 border border-slate-600 rounded-lg shadow-xl text-xs text-slate-200 leading-relaxed"
          style={{ top: pos.top, left: pos.left, transform: 'translate(-50%, -100%)' }}
        >
          <div className="font-semibold text-indigo-400 mb-1">{productName || 'Product'} Fit</div>
          {reasoning}
          <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-slate-600" />
        </div>
      )}
    </>
  );
}

export default function LeadsTable({ onRefresh }) {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [successMsg, setSuccessMsg] = useState('');
  const [page, setPage] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [pageSize] = useState(50);

  // Filters
  const [search, setSearch] = useState('');
  const [bucket, setBucket] = useState('');
  const [source, setSource] = useState('');
  const [fitFilter, setFitFilter] = useState('');
  const [enrichmentStatus, setEnrichmentStatus] = useState('');

  // Data for dropdowns
  const [buckets, setBuckets] = useState([]);
  const [sources, setSources] = useState([]);

  const [sortBy, setSortBy] = useState('created_at');
  const [sortDir, setSortDir] = useState('desc');

  // Multi-select
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [showBulkMoveBucket, setShowBulkMoveBucket] = useState(false);

  // Best rep data cache
  // bestRepCache removed — sales rep matching disabled
  // Sequences feature removed in cleanup pass.

  useEffect(() => {
    loadBucketsAndSources();
  }, []);

  useEffect(() => {
    setPage(0);
    loadLeads();
  }, [search, bucket, source, fitFilter, enrichmentStatus, sortBy, sortDir]);

  useEffect(() => {
    loadLeads();
  }, [page]);

  const loadBucketsAndSources = async () => {
    try {
      const [bucketsData, sourcesData] = await Promise.all([
        api.getBuckets(),
        api.getSources()
      ]);
      setBuckets(bucketsData || []);
      setSources(sourcesData || []);
    } catch (err) {
      console.error('Failed to load buckets/sources:', err);
    }
  };

  const loadLeads = async () => {
    try {
      setLoading(true);
      const params = {
        limit: pageSize,
        offset: page * pageSize,
        sort_by: sortBy,
        sort_dir: sortDir,
      };

      if (search) params.search = search;
      if (bucket) params.bucket = bucket;
      if (source) params.source_id = source;
      if (fitFilter) params.fit = fitFilter;
      if (enrichmentStatus) params.enrichment_status = enrichmentStatus;

      const data = await api.getLeads(params);
      setLeads(data.leads || []);
      setTotalCount(data.total || 0);
      setError('');
    } catch (err) {
      setError(err.message);
      setLeads([]);
    } finally {
      setLoading(false);
    }
  };

  const handleExportCsv = async () => {
    try {
      setLoading(true);
      setError('');
      const qs = new URLSearchParams(
        Object.fromEntries(
          Object.entries({ bucket, fit: fitFilter, enrichment_status: enrichmentStatus }).filter(([, v]) => v)
        )
      ).toString();
      await api.downloadFile(`/api/export/csv${qs ? '?' + qs : ''}`, { filename: 'leads.csv' });
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleExportHubspot = async () => {
    try {
      setLoading(true);
      setError('');
      setSuccessMsg('');
      const result = await api.fetch('/api/export/hubspot', { method: 'POST' });
      setSuccessMsg(`HubSpot export queued (${result.job_id}). Check the Jobs panel for progress.`);
      setTimeout(() => setSuccessMsg(''), 5000);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleBulkDedup = async () => {
    try {
      setLoading(true);
      setError('');
      setSuccessMsg('');
      const result = await api.startDedup();
      setSuccessMsg(`Dedup job queued (${result.job_id}). Check the Jobs panel for progress.`);
      setTimeout(() => setSuccessMsg(''), 5000);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteLead = async (id) => {
    if (!window.confirm('Delete this lead?')) return;
    try {
      await api.deleteLead(id);
      await loadLeads();
    } catch (err) {
      setError(err.message);
    }
  };

  const toggleSelectLead = (id) => {
    const newSelected = new Set(selectedIds);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedIds(newSelected);
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === leads.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(leads.map(l => l.id)));
    }
  };

  const handleSoftEnrich = async () => {
    try {
      setLoading(true);
      setError('');
      setSuccessMsg('');
      const result = await api.softEnrich();
      const parts = [];
      if (result.names_from_fullname) parts.push(`${result.names_from_fullname} names from full names`);
      if (result.names_from_email) parts.push(`${result.names_from_email} names from emails`);
      if (result.company_from_email) parts.push(`${result.company_from_email} companies from emails`);
      if (result.title_from_headline) parts.push(`${result.title_from_headline} titles from headlines`);
      if (result.industry_from_research) parts.push(`${result.industry_from_research} industries from research`);
      const msg = parts.length > 0
        ? `Soft enrich done: ${parts.join(', ')}.`
        : 'Soft enrich done — no gaps to fill.';
      setSuccessMsg(msg);
      setTimeout(() => setSuccessMsg(''), 8000);
      await loadLeads();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleBulkEnrich = async () => {
    if (selectedIds.size === 0) return;
    try {
      setLoading(true);
      setError('');
      setSuccessMsg('');
      const result = await api.bulkEnrich(Array.from(selectedIds));
      setSuccessMsg(`Enrichment job queued (${result.job_id}). Check the Jobs panel for progress.`);
      setTimeout(() => setSuccessMsg(''), 5000);
      setSelectedIds(new Set());
      await new Promise(r => setTimeout(r, 1000));
      await loadLeads();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleBulkScore = async () => {
    if (selectedIds.size === 0) return;
    try {
      setLoading(true);
      setError('');
      setSuccessMsg('');
      const result = await api.bulkScore(Array.from(selectedIds));
      setSuccessMsg(`ICP scoring job queued (${result.job_id}). Check the Jobs panel for progress.`);
      setTimeout(() => setSuccessMsg(''), 5000);
      setSelectedIds(new Set());
      await new Promise(r => setTimeout(r, 1000));
      await loadLeads();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleBulkRoute = async () => {
    if (selectedIds.size === 0) return;
    try {
      setLoading(true);
      await api.bulkRoute(Array.from(selectedIds));
      setSelectedIds(new Set());
      await new Promise(r => setTimeout(r, 1000));
      await loadLeads();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleBulkMoveBucket = async (bucketId) => {
    if (selectedIds.size === 0) return;
    try {
      setLoading(true);
      await api.bulkMoveToBucket(Array.from(selectedIds), bucketId);
      setShowBulkMoveBucket(false);
      setSelectedIds(new Set());
      await loadLeads();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleBulkDelete = async () => {
    if (selectedIds.size === 0 || !window.confirm(`Delete ${selectedIds.size} leads?`)) return;
    try {
      setLoading(true);
      await api.bulkDelete(Array.from(selectedIds));
      setSelectedIds(new Set());
      await loadLeads();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleBulkDnc = async () => {
    if (selectedIds.size === 0) return;
    const reason = window.prompt(
      `Mark ${selectedIds.size} lead(s) as Do Not Contact. Optional reason:`,
      ''
    );
    if (reason === null) return; // cancelled
    try {
      setLoading(true);
      await api.bulkMarkLeadsDnc(Array.from(selectedIds), { reason: reason || null, dnc: true });
      setSuccessMsg(`${selectedIds.size} lead(s) marked DNC.`);
      setTimeout(() => setSuccessMsg(''), 4000);
      setSelectedIds(new Set());
      await loadLeads();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleRowDnc = async (lead) => {
    const reason = window.prompt(
      `Mark "${lead.full_name || lead.email || lead.id}" as Do Not Contact. Optional reason:`,
      ''
    );
    if (reason === null) return;
    try {
      await api.markLeadDnc(lead.id, reason || null);
      await loadLeads();
      onRefresh?.();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleBulkReVerify = async () => {
    if (selectedIds.size === 0) return;
    try {
      setLoading(true);
      const result = await api.bulkReVerifyEmployment(Array.from(selectedIds));
      setSuccessMsg(`Re-verification job queued (${result.job_id}).`);
      setTimeout(() => setSuccessMsg(''), 5000);
      setSelectedIds(new Set());
      await loadLeads();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleResolveTitles = async () => {
    if (selectedIds.size === 0) return;
    try {
      setLoading(true);
      setError('');
      setSuccessMsg('');
      const result = await api.resolveTitles(Array.from(selectedIds));
      setSuccessMsg(`Title resolution job queued (${result.job_id}). Check the Jobs panel for progress.`);
      setTimeout(() => setSuccessMsg(''), 5000);
      setSelectedIds(new Set());
      await new Promise(r => setTimeout(r, 1000));
      await loadLeads();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSort = (column) => {
    if (sortBy === column) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(column);
      setSortDir('desc');
    }
  };

  const startIdx = page * pageSize;
  const endIdx = Math.min(startIdx + pageSize, totalCount);

  const SortIcon = ({ column }) => {
    if (sortBy !== column) return <span className="text-slate-600">⇅</span>;
    return <span className="text-indigo-400">{sortDir === 'asc' ? '↑' : '↓'}</span>;
  };

  return (
    <div className="h-full flex flex-col">
      {/* Top Bar */}
      <div className="p-6 border-b border-slate-700 space-y-4 flex-shrink-0">
        {error && (
          <div className="p-3 bg-red-500/20 border border-red-500/50 rounded text-red-300 text-sm">
            {error}
          </div>
        )}
        {successMsg && (
          <div className="p-3 bg-emerald-500/20 border border-emerald-500/50 rounded text-emerald-300 text-sm">
            {successMsg}
          </div>
        )}

        {/* Search and Filters */}
        <div className="space-y-3">
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search leads by name, title, company..."
            className="w-full bg-slate-700 text-slate-100 px-4 py-2 rounded border border-slate-600 focus:border-indigo-500 focus:outline-none"
          />

          <div className="grid grid-cols-5 gap-3">
            <select
              value={bucket}
              onChange={(e) => setBucket(e.target.value)}
              className="bg-slate-700 text-slate-100 px-3 py-2 rounded border border-slate-600 text-sm"
            >
              <option value="">All Buckets</option>
              {buckets.map((b) => (
                <option key={b.id} value={b.id}>{b.name}</option>
              ))}
            </select>

            <select
              value={source}
              onChange={(e) => setSource(e.target.value)}
              className="bg-slate-700 text-slate-100 px-3 py-2 rounded border border-slate-600 text-sm"
            >
              <option value="">All Sources</option>
              {sources.map((s) => (
                <option key={s.id} value={s.id}>{s.name}</option>
              ))}
            </select>

            <select
              value={fitFilter}
              onChange={(e) => setFitFilter(e.target.value)}
              className="bg-slate-700 text-slate-100 px-3 py-2 rounded border border-slate-600 text-sm"
            >
              <option value="">All Fit Levels</option>
              <option value="high">High Fit</option>
              <option value="medium">Medium Fit</option>
              <option value="low">Low Fit</option>
              <option value="unscored">Unscored</option>
            </select>

            <select
              value={enrichmentStatus}
              onChange={(e) => setEnrichmentStatus(e.target.value)}
              className="bg-slate-700 text-slate-100 px-3 py-2 rounded border border-slate-600 text-sm"
            >
              <option value="">All Statuses</option>
              <option value="pending">Pending</option>
              <option value="enriched">Enriched</option>
              <option value="failed">Failed</option>
            </select>
          </div>
        </div>

        {/* Bulk Action Bar */}
        {selectedIds.size > 0 && (
          <div className="p-4 bg-indigo-500/10 border border-indigo-500/30 rounded-lg flex items-center justify-between flex-wrap gap-3">
            <span className="text-sm text-indigo-300 font-medium">{selectedIds.size} lead(s) selected</span>
            <div className="flex gap-2 flex-wrap">
              <button
                onClick={handleBulkEnrich}
                disabled={loading}
                className="px-3 py-1.5 bg-indigo-600 hover:bg-indigo-500 text-white rounded text-xs font-medium transition-colors disabled:opacity-50"
              >
                Enrich
              </button>
              <button
                onClick={handleBulkScore}
                disabled={loading}
                className="px-3 py-1.5 bg-indigo-600 hover:bg-indigo-500 text-white rounded text-xs font-medium transition-colors disabled:opacity-50"
              >
                Score
              </button>
              <button
                onClick={handleBulkRoute}
                disabled={loading}
                className="px-3 py-1.5 bg-indigo-600 hover:bg-indigo-500 text-white rounded text-xs font-medium transition-colors disabled:opacity-50"
              >
                Route
              </button>
              <button
                onClick={() => setShowBulkMoveBucket(true)}
                disabled={loading}
                className="px-3 py-1.5 bg-indigo-600 hover:bg-indigo-500 text-white rounded text-xs font-medium transition-colors disabled:opacity-50"
              >
                Move to bucket
              </button>
              <button
                onClick={handleBulkReVerify}
                disabled={loading}
                className="px-3 py-1.5 bg-amber-600 hover:bg-amber-500 text-white rounded text-xs font-medium transition-colors disabled:opacity-50"
              >
                Re-verify
              </button>
              <button
                onClick={handleResolveTitles}
                disabled={loading}
                className="px-3 py-1.5 bg-cyan-600 hover:bg-cyan-500 text-white rounded text-xs font-medium transition-colors disabled:opacity-50"
              >
                Resolve titles
              </button>
              <button
                onClick={handleBulkDnc}
                disabled={loading}
                className="px-3 py-1.5 bg-red-800 hover:bg-red-700 text-white rounded text-xs font-medium transition-colors disabled:opacity-50"
                title="Mark selected leads as Do Not Contact"
              >
                Mark DNC
              </button>
              <button
                onClick={handleBulkDelete}
                disabled={loading}
                className="px-3 py-1.5 bg-red-600 hover:bg-red-500 text-white rounded text-xs font-medium transition-colors disabled:opacity-50"
              >
                Delete
              </button>
              <button
                onClick={() => setSelectedIds(new Set())}
                disabled={loading}
                className="px-3 py-1.5 bg-slate-700 hover:bg-slate-600 text-slate-200 rounded text-xs font-medium transition-colors disabled:opacity-50"
              >
                Deselect All
              </button>
            </div>
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex gap-2 flex-wrap">
          <button
            onClick={handleSoftEnrich}
            disabled={loading}
            className="px-4 py-2 bg-emerald-600/20 hover:bg-emerald-600/30 text-emerald-400 rounded text-sm font-medium transition-colors disabled:opacity-50"
          >
            Soft Enrich
          </button>
          <button
            onClick={handleBulkEnrich}
            disabled={loading}
            className="px-4 py-2 bg-indigo-600/20 hover:bg-indigo-600/30 text-indigo-400 rounded text-sm font-medium transition-colors disabled:opacity-50"
          >
            Enrich All
          </button>
          <button
            onClick={handleBulkScore}
            disabled={loading}
            className="px-4 py-2 bg-indigo-600/20 hover:bg-indigo-600/30 text-indigo-400 rounded text-sm font-medium transition-colors disabled:opacity-50"
          >
            Score All
          </button>
          <button
            onClick={handleBulkRoute}
            disabled={loading}
            className="px-4 py-2 bg-indigo-600/20 hover:bg-indigo-600/30 text-indigo-400 rounded text-sm font-medium transition-colors disabled:opacity-50"
          >
            Route All
          </button>
          <button
            onClick={handleBulkDedup}
            disabled={loading}
            className="px-4 py-2 bg-indigo-600/20 hover:bg-indigo-600/30 text-indigo-400 rounded text-sm font-medium transition-colors disabled:opacity-50"
          >
            Dedup
          </button>

          <div className="w-px h-6 bg-slate-700 mx-1" />

          <button
            onClick={handleExportHubspot}
            disabled={loading}
            className="px-4 py-2 bg-orange-600/20 hover:bg-orange-600/30 text-orange-400 rounded text-sm font-medium transition-colors disabled:opacity-50"
          >
            Export to HubSpot
          </button>
          <button
            onClick={handleExportCsv}
            disabled={loading}
            className="px-4 py-2 bg-slate-600/20 hover:bg-slate-600/30 text-slate-300 rounded text-sm font-medium transition-colors disabled:opacity-50"
          >
            Export CSV
          </button>
        </div>
      </div>

      {/* Table */}
      <div className="flex-1 overflow-auto scrollable-content">
        {leads.length === 0 ? (
          <div className="flex items-center justify-center h-full text-center">
            <div className="text-slate-400">
              <p className="text-lg font-medium mb-1">No leads yet</p>
              <p className="text-sm">Add a source and run a scrape to get started.</p>
            </div>
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="sticky top-0 bg-slate-800 border-b border-slate-700">
              <tr>
                <th className="px-4 py-4 text-left font-semibold text-slate-200 w-12">
                  <input
                    type="checkbox"
                    checked={selectedIds.size === leads.length && leads.length > 0}
                    onChange={toggleSelectAll}
                    className="w-4 h-4 cursor-pointer"
                  />
                </th>
                <th
                  onClick={() => handleSort('name')}
                  className="px-6 py-4 text-left font-semibold text-slate-200 cursor-pointer hover:bg-slate-700/50"
                >
                  <div className="flex items-center gap-2">
                    Name <SortIcon column="name" />
                  </div>
                </th>
                <th
                  onClick={() => handleSort('title')}
                  className="px-6 py-4 text-left font-semibold text-slate-200 cursor-pointer hover:bg-slate-700/50"
                >
                  <div className="flex items-center gap-2">
                    Title <SortIcon column="title" />
                  </div>
                </th>
                <th
                  onClick={() => handleSort('company')}
                  className="px-6 py-4 text-left font-semibold text-slate-200 cursor-pointer hover:bg-slate-700/50"
                >
                  <div className="flex items-center gap-2">
                    Company <SortIcon column="company" />
                  </div>
                </th>
                <th className="px-6 py-4 text-left font-semibold text-slate-200">Email</th>
                <th className="px-6 py-4 text-left font-semibold text-slate-200">
                  Product Fit
                </th>
                <th className="px-6 py-4 text-left font-semibold text-slate-200">Owner</th>
                <th className="px-6 py-4 text-left font-semibold text-slate-200">Bucket</th>
                <th className="px-6 py-4 text-left font-semibold text-slate-200">Status</th>
                {/* Best Rep column removed — too intensive for not much value */}
                <th className="px-6 py-4 text-left font-semibold text-slate-200">Actions</th>
              </tr>
            </thead>
            <tbody>
              {leads.map((lead) => (
                <tr
                  key={lead.id}
                  className={`border-b border-slate-700/50 transition-colors ${selectedIds.has(lead.id) ? 'bg-indigo-500/10' : 'hover:bg-slate-800/50'}`}
                >
                  <td className="px-4 py-4">
                    <input
                      type="checkbox"
                      checked={selectedIds.has(lead.id)}
                      onChange={() => toggleSelectLead(lead.id)}
                      className="w-4 h-4 cursor-pointer"
                    />
                  </td>
                  <td className="px-6 py-4">
                    <span className="text-slate-100 font-medium">{lead.full_name || [lead.first_name, lead.last_name].filter(Boolean).join(' ') || '-'}</span>
                  </td>
                  <td className="px-6 py-4 text-slate-400">{lead.title || '-'}</td>
                  <td className="px-6 py-4 text-slate-400">{lead.company || '-'}</td>
                  <td className="px-6 py-4 text-slate-400 truncate">
                    <div className="flex items-center gap-1.5">
                      {lead.suppressed && (
                        <span className="flex-shrink-0 w-4 h-4 bg-red-500/20 text-red-400 rounded text-[10px] font-bold flex items-center justify-center" title={`DNC: ${lead.suppression_reason || ''}`}>
                          &#x2715;
                        </span>
                      )}
                      {lead.email || '-'}
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    {lead.product_scores && lead.product_scores.length > 0 ? (
                      <div className="flex flex-wrap gap-1.5">
                        {lead.product_scores.map((ps) => (
                          <FitTooltip key={ps.product_id} productName={ps.product_name} reasoning={ps.reasoning}>
                            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium cursor-default ${getFitBadgeColor(ps.fit)}`}>
                              <span className="text-slate-400 font-normal max-w-[60px] truncate">{ps.product_name}</span>
                              {fitLabel(ps.fit)}
                            </span>
                          </FitTooltip>
                        ))}
                      </div>
                    ) : (
                      <span className="text-slate-500 text-xs">Unscored</span>
                    )}
                  </td>
                  <td className="px-6 py-4">
                    {lead.owner ? (
                      <span className="bg-indigo-500/20 text-indigo-400 px-2 py-0.5 rounded text-xs font-medium">
                        {lead.owner}
                      </span>
                    ) : (
                      <span className="text-slate-500 text-xs">Unassigned</span>
                    )}
                  </td>
                  <td className="px-6 py-4">
                    {lead.bucket ? (
                      <span className="bg-slate-700 text-slate-200 px-2.5 py-1 rounded text-xs">
                        {lead.bucket}
                      </span>
                    ) : (
                      <span className="text-slate-500 text-xs">Unrouted</span>
                    )}
                  </td>
                  <td className="px-6 py-4">
                    <span className="text-xs px-2.5 py-1 bg-slate-700/50 text-slate-300 rounded">
                      {lead.enrichment_status || 'Unenriched'}
                    </span>
                  </td>
                  {/* Best Rep cell removed */}
                  <td className="px-6 py-4">
                    <div className="flex gap-3">
                      <button
                        onClick={() => handleRowDnc(lead)}
                        className="text-amber-400 hover:text-amber-300 text-xs font-medium"
                        title="Mark as Do Not Contact"
                      >
                        DNC
                      </button>
                      <button
                        onClick={() => handleDeleteLead(lead.id)}
                        className="text-red-400 hover:text-red-300 text-xs font-medium"
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Pagination */}
      {leads.length > 0 && (
        <div className="px-6 py-4 border-t border-slate-700 flex items-center justify-between flex-shrink-0">
          <p className="text-sm text-slate-400">
            Showing {startIdx + 1}-{endIdx} of {totalCount} leads
          </p>
          <div className="flex gap-2">
            <button
              onClick={() => setPage(Math.max(0, page - 1))}
              disabled={page === 0 || loading}
              className="px-4 py-2 bg-slate-700 hover:bg-slate-600 disabled:bg-slate-800 text-slate-100 rounded text-sm font-medium transition-colors disabled:opacity-50"
            >
              Previous
            </button>
            <button
              onClick={() => setPage(page + 1)}
              disabled={endIdx >= totalCount || loading}
              className="px-4 py-2 bg-slate-700 hover:bg-slate-600 disabled:bg-slate-800 text-slate-100 rounded text-sm font-medium transition-colors disabled:opacity-50"
            >
              Next
            </button>
          </div>
        </div>
      )}

      {/* Move to bucket modal */}
      {showBulkMoveBucket && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50" onClick={() => setShowBulkMoveBucket(false)}>
          <div className="bg-slate-800 border border-slate-600 rounded-xl w-full max-w-sm p-6" onClick={e => e.stopPropagation()}>
            <h3 className="text-lg font-bold text-slate-100 mb-4">Move to Bucket</h3>
            <div className="space-y-2">
              {buckets.map(b => (
                <button
                  key={b.id}
                  onClick={() => handleBulkMoveBucket(b.id)}
                  className="w-full text-left px-4 py-2 bg-slate-700 hover:bg-slate-600 text-slate-100 rounded transition-colors"
                >
                  {b.name}
                </button>
              ))}
            </div>
            <button
              onClick={() => setShowBulkMoveBucket(false)}
              className="w-full mt-4 px-4 py-2 text-sm text-slate-400 hover:text-slate-200"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

    </div>
  );
}
